import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import {
  useCreateOrAssignEmailCycleStore,
  useEmailCycleNamesStore,
} from "../stores/Hooks";
import {
  LoadingIndicator,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { ChooseEmailCycleDialog } from "./dialogs/ChooseEmailCycleDialog";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../routes/RoutesHelper";
import { UnexpectedErrorDialog } from "../../../components/dialogs/UnexpectedErrorDialog";
import { Trans, useTranslation } from "react-i18next";
import { PendingSurveyAssignmentErrorDialog } from "./dialogs/PendingSurveyAssignmentErrorDialog";
import { GetEmailCycleNamesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleNamesRPC";
import { SuccessDialog } from "../../../components/dialogs/SuccessDialog";
import { useRespondentStore } from "../../store/Hooks";
import { CreateEmailCycle } from "./CreateEmailCycle";
import { MissingEmailIdErrorDialog } from "./dialogs/MissingEmailIdErrorDialog";
import { ErrorDialog } from "../../../components/dialogs/ErrorDialog";

interface CreateOrAssignEmailCycleProps {
  closeFlow: VoidFunction;
}

export const CreateOrAssignEmailCycle: React.FC<CreateOrAssignEmailCycleProps> =
  observer(({ closeFlow }) => {
    const [openChooseEmailCycleDialog, setOpenChooseEmailCycleDialog] =
      useState(true);
    const [
      openGetEmailCycleNamesUnexpectedErrorDialog,
      setOpenGetEmailCycleNamesUnexpectedErrorDialog,
    ] = useState(false);
    const [
      openEmailQueuedSuccessfulDialog,
      setOpenEmailQueuedSuccessfulDialog,
    ] = useState(false);
    const [assignedEmailCyclesCount, setAssignedEmailCyclesCount] = useState(0);

    const { t } = useTranslation();
    const typography = useTypography();
    const navigateTo = useRouteNavigator();
    const flowHistories = useRef<VoidFunction[]>([]);

    const respondentStore = useRespondentStore();
    const emailCycleNamesStore = useEmailCycleNamesStore();
    const createOrAssignEmailCycleStore = useCreateOrAssignEmailCycleStore();

    const getEmailCycleNames = (): void => {
      processProjectParams(
        emailCycleNamesStore.getEmailCycleNames,
        navigateTo,
      ).catch(() => setOpenGetEmailCycleNamesUnexpectedErrorDialog(true));
    };

    // Fetching email cycle names on load
    useEffect(getEmailCycleNames, []);

    // Rest store values
    useEffect(
      () => () => {
        emailCycleNamesStore.resetStore();
        createOrAssignEmailCycleStore.resetStore();
      },
      [],
    );

    if (emailCycleNamesStore.loadingEmailCycleNames) {
      return <LoadingIndicator variant="overlay" isLoading />;
    }

    if (createOrAssignEmailCycleStore.pendingSurveyAssignmentError) {
      return (
        <PendingSurveyAssignmentErrorDialog
          pendingSurveyAssignmentError={
            createOrAssignEmailCycleStore.pendingSurveyAssignmentError
          }
          closeDialog={closeFlow}
        />
      );
    }

    if (createOrAssignEmailCycleStore.missingEmailIdError) {
      return (
        <MissingEmailIdErrorDialog
          missingEmailIdCount={
            createOrAssignEmailCycleStore.missingEmailIdError
          }
          closeDialog={closeFlow}
        />
      );
    }

    if (openGetEmailCycleNamesUnexpectedErrorDialog) {
      return (
        <UnexpectedErrorDialog
          message={t("emailCycles.getEmailCycleNamesUnexpectedErrorMessage")}
          onClickDone={closeFlow}
        />
      );
    }

    if (openEmailQueuedSuccessfulDialog) {
      const respondentsCount = respondentStore.multiSelectTableStore
        .areAllRowsSelected
        ? respondentStore.totalItems
        : respondentStore.multiSelectTableStore.selectedRowIds.length;

      return (
        <SuccessDialog
          title={t("emailCycles.emailQueuedSuccessfulTitle")}
          message={
            <Trans
              i18nKey="emailCycles.emailQueuedSuccessfulMessage"
              values={{
                respondentsCount,
                emailCyclesCount: assignedEmailCyclesCount,
              }}
              components={{
                span: <span style={typography.s1} />,
              }}
            />
          }
          onClickClose={closeFlow}
        />
      );
    }

    // Handling RPC Errors
    if (emailCycleNamesStore.getEmailCycleNamesRPCError) {
      switch (emailCycleNamesStore.getEmailCycleNamesRPCError) {
        case GetEmailCycleNamesRPC.RPCError.InvalidProjectId:
          navigateTo.pageNotFound();
          break;
        case GetEmailCycleNamesRPC.RPCError.ProjectAlreadyArchived:
          return (
            <ErrorDialog
              title={t("common.projectAlreadyArchived")}
              message={t("common.projectAlreadyArchivedErrorText")}
              onClickClose={closeFlow}
            />
          );
        default:
          emailCycleNamesStore.getEmailCycleNamesRPCError satisfies never;
      }
      return null;
    }

    if (
      emailCycleNamesStore.emailCycleNames.length &&
      openChooseEmailCycleDialog
    ) {
      return (
        <ChooseEmailCycleDialog
          onAssignmentSuccess={(emailCyclesCount) => {
            setAssignedEmailCyclesCount(emailCyclesCount);
            setOpenEmailQueuedSuccessfulDialog(true);
          }}
          handleOpenCreateEmailCycleDrawer={() => {
            flowHistories.current.push(() =>
              setOpenChooseEmailCycleDialog(true),
            );
            setOpenChooseEmailCycleDialog(false);
          }}
          getEmailCycleNames={getEmailCycleNames}
          closeDialog={closeFlow}
        />
      );
    } else {
      return (
        <CreateEmailCycle
          setOpenEmailQueuedSuccessfulDialog={(isOpen) => {
            setAssignedEmailCyclesCount(1);
            setOpenEmailQueuedSuccessfulDialog(isOpen);
          }}
          closeFlow={() => {
            const flowHistory = flowHistories.current.pop();
            flowHistory ? flowHistory() : closeFlow();
          }}
        />
      );
    }
  });
